import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthResponse } from '../types';
import { apiService } from '../services/api';
import {LoginGoogleRequest} from "../types/DTOs/LoginGoogleRequest";
import {Utility} from "../utils/Utility";
import {Log} from "../utils/Log";

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, username: string) => Promise<void>;
  //googleLogin: () => void;
  googleLogin: (credentialResponse: any) => Promise<void>;
  linkedinLogin: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');
        
        if (token && userData) {
          setUser(JSON.parse(userData));
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  function setUserLocal(response: AuthResponse){
    localStorage.setItem('token', response.token);
    localStorage.setItem('user', JSON.stringify(response.user));
    setUser(response.user);
  }

  const login = async (email: string, password: string) => {
    try {
      const response: AuthResponse = await apiService.login({ email, password });

      setUserLocal(response);
      //localStorage.setItem('token', response.token);
      //localStorage.setItem('user', JSON.stringify(response.user));
      //setUser(response.user);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const register = async (email: string, password: string, username: string) => {
    try {
      const response: AuthResponse = await apiService.register({ email, password, username });
      
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      setUser(response.user);
    } catch (error) {
      console.error('Register error:', error);
      throw error;
    }
  };

  const googleLogin = async (credentialResponse: any) => {
    try {
      let userInfo: any;

      if (typeof credentialResponse?.credential === 'string') {
        // JWT token da Google
        userInfo = JSON.parse(atob(credentialResponse.credential.split('.')[1]));
      } else if (credentialResponse?.userInfo) {
        // Già decodificato da una chiamata API
        userInfo = credentialResponse.userInfo;
      } else {
        throw new Error('Formato risposta Google non valido');
      }

      Log.writeLogDebug('Dati utente: ' + JSON.stringify(userInfo));

      const logineDataRequest :LoginGoogleRequest ={
        googleId: userInfo.sub,
        email: userInfo.email,
        nameAll: userInfo.name,
        picture: userInfo.picture,
        name: userInfo.given_name,
        surname :userInfo.family_name
      }

      console.log('Payload Google:', JSON.stringify(logineDataRequest));

      const response = await apiService.loginGoogle(logineDataRequest);

      Log.writeLogDebug(response);

      setUserLocal(response);

    } catch (err) {
      console.error('Errore:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const linkedinLogin = () => {
    apiService.linkedinLogin();
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    googleLogin,
    linkedinLogin,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

